import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Plus, Phone, Mail, Download, Upload, Trash2, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { removeMany, patchMany, confirmCount } from '../../lib/bulkRows';
import { apiLeads } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import { toast } from 'sonner';

const stages = ['New', 'Contacted', 'Qualified', 'Proposal Sent', 'Negotiation', 'Won', 'Lost'];
const stageColor = (s) => ({ New: 'bg-slate-100 text-slate-700', Contacted: 'bg-blue-100 text-blue-700', Qualified: 'bg-purple-100 text-purple-700', 'Proposal Sent': 'bg-amber-100 text-amber-700', Negotiation: 'bg-orange-100 text-orange-700', Won: 'bg-emerald-100 text-emerald-700', Lost: 'bg-red-100 text-red-700' }[s]);

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { apiLeads.list().then(d => setLeads(d || [])).catch(() => setLeads([])); }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (lead, dir) => {
    const i = stages.indexOf(lead.stage);
    const ni = Math.max(0, Math.min(stages.length - 1, i + dir));
    try { await apiLeads.update(lead.id, { ...lead, stage: stages[ni] }); load(); } catch { toast.error('Failed'); }
  };
  const onDelete = async (id) => { try { await apiLeads.remove(id); load(); toast.success('Lead removed'); } catch { toast.error('Failed'); } };
  const onAdd = async (l) => { try { await apiLeads.create(l); load(); toast.success('Lead added'); } catch { toast.error('Failed'); } };

  const onExport = () => {
    downloadFile('xpertpests-leads.csv', toCSV(leads, [{ key: 'id', header: 'ID' }, { key: 'name', header: 'Company' }, { key: 'contact', header: 'Contact' }, { key: 'email', header: 'Email' }, { key: 'phone', header: 'Phone' }, { key: 'value', header: 'Value' }, { key: 'source', header: 'Source' }, { key: 'stage', header: 'Stage' }]));
    toast.success('Exported leads.csv');
  };
  const onImport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const parsed = parseCSV(await readFileAsText(f));
    const mapped = parsed.map((r, i) => ({ id: r.ID || `L-imp-${Date.now()}-${i}`, name: r.Company || '', contact: r.Contact || '', email: r.Email || '', phone: r.Phone || '', value: Number(r.Value) || 0, source: r.Source || 'Import', stage: r.Stage || 'New', notes: '' }));
    try { await apiLeads.bulk(mapped); load(); toast.success(`Imported ${mapped.length} leads`); } catch { toast.error('Import failed'); }
    e.target.value = '';
  };

  // Ticking cards. The board moves one lead one stage at a time, which is fine
  // for a live pipeline and hopeless for a hundred imported rows.
  const sel = useSelection(leads.map((l) => l.id));
  const picked = () => leads.filter((l) => sel.has(l.id));

  const bulkStage = async (stage) => {
    const rows = picked();
    if (!rows.length) return;
    await patchMany(apiLeads, rows, { stage }, `lead(s) moved to ${stage}`);
    sel.clear();
    load();
  };

  const bulkDelete = async () => {
    const ids = sel.chosen;
    if (!ids.length) return;
    if (!confirmCount('Delete', ids.length, 'lead', 'This cannot be undone.')) return;
    await removeMany(apiLeads, ids, 'lead');
    sel.clear();
    load();
  };

  const grouped = stages.reduce((acc, s) => { acc[s] = leads.filter(l => l.stage === s); return acc; }, {});
  const totals = stages.map(s => grouped[s].reduce((a, l) => a + (Number(l.value) || 0), 0));
  const grandTotal = totals.reduce((a, b) => a + b, 0);
  const wonTotal = grouped.Won.reduce((a, l) => a + (Number(l.value) || 0), 0);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="leads-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Sales Pipeline</h1><p className="text-slate-500 mt-1">{leads.length} leads · ${grandTotal.toLocaleString()} potential · ${wonTotal.toLocaleString()} won</p></div>
        <div className="flex gap-2">
          <label className="inline-flex items-center gap-1.5 px-4 h-10 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-4 h-4" />Import CSV<input type="file" accept=".csv" className="hidden" onChange={onImport} /></label>
          <Button variant="outline" onClick={onExport} className="gap-1.5"><Download className="w-4 h-4" />Export</Button>
          <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-lead-btn"><Plus className="w-4 h-4" />New Lead</Button>
        </div>
      </div>
      {sel.count > 0 && (
        <Card className="border-[#8BC53F]/30 overflow-hidden">
          <BulkBar count={sel.count} onClear={sel.clear} testId="leads-bulk-bar"
                   countTestId="leads-bulk-count">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" data-testid="leads-bulk-stage-btn">
                  Move to <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                {stages.map((st) => (
                  <DropdownMenuItem key={st} onClick={() => bulkStage(st)} data-testid={`leads-bulk-stage-${st}`}>
                    {st}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" className="h-8 gap-1 bg-white text-red-600 hover:text-red-700" onClick={bulkDelete} data-testid="leads-bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5" />Delete
            </Button>
          </BulkBar>
        </Card>
      )}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((s, idx) => (
          <div key={s} className="flex-1 min-w-[260px]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Badge className={`${stageColor(s)} border-0 font-bold`}>{s}</Badge><span className="text-xs text-slate-500 font-medium">{grouped[s].length}</span></div>
              <span className="text-xs text-slate-500 tabular-nums">${totals[idx].toLocaleString()}</span>
            </div>
            <div className="space-y-2">
              {grouped[s].map(l => (
                <Card key={l.id} className={`p-3 border-slate-200 hover:shadow-md transition pp-lift ${sel.has(l.id) ? 'ring-2 ring-[#8BC53F]' : ''}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      <span className="pt-0.5">
                        <TickBox checked={sel.has(l.id)} onChange={() => sel.toggle(l.id)}
                                 label={`Select ${l.name || l.id}`} testId={`leads-select-${l.id}`}/>
                      </span>
                      <div className="font-bold text-slate-900 text-sm truncate">{l.name}</div>
                    </div>
                    <span className="text-xs font-bold text-emerald-600 tabular-nums">${(Number(l.value) || 0).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">{l.contact}</div>
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500"><Phone className="w-3 h-3" />{l.phone}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</div>
                  {l.notes && <div className="text-[11px] text-slate-600 mt-2 italic line-clamp-2">{l.notes}</div>}
                  <div className="flex gap-1 mt-2 items-center">
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-1" disabled={stages.indexOf(l.stage) === 0} onClick={() => move(l, -1)}>←</Button>
                    <button onClick={() => onDelete(l.id)} className="text-slate-300 hover:text-red-600 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-1" disabled={stages.indexOf(l.stage) === stages.length - 1} onClick={() => move(l, 1)}>→</Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
      <LeadDialog open={open} setOpen={setOpen} onAdd={onAdd} />
    </div>
  );
}

function LeadDialog({ open, setOpen, onAdd }) {
  const [form, setForm] = useState({ name: '', contact: '', email: '', phone: '', value: '', source: 'Website', notes: '' });
  const submit = () => {
    if (!form.name) { toast.error('Company name required'); return; }
    onAdd({ id: `L-${Date.now().toString(36)}`, ...form, value: Number(form.value) || 0, stage: 'New' });
    setOpen(false); setForm({ name: '', contact: '', email: '', phone: '', value: '', source: 'Website', notes: '' });
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="lead-dialog">
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Company</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="lead-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact</Label><Input value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Value $</Label><Input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
            <div className="col-span-2"><Label>Source</Label><Input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="mt-1.5" /></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="lead-save-btn">Add Lead</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
